// The weather forecast contract (docs/specs/weather.md §2): what a weather driver gives as one
// reading, and the forecast tile reads. Here, beside the driver contract, so that drivers make it
// and tiles read it without knowing each other. Times are ms since the epoch; `windDirection` is
// where the wind comes from, in degrees; `chance` is 0 to 1.

/** The kinds of weather, from the mildest to the most severe (spec §5). */
export const WEATHER_KINDS = [
    'clear',
    'few-clouds',
    'partly-cloudy',
    'cloudy',
    'overcast',
    'fog',
    'haze',
    'drizzle',
    'showers',
    'rain',
    'heavy-rain',
    'freezing-rain',
    'sleet',
    'snow-showers',
    'snow',
    'thunderstorm',
    'squall',
    'tornado',
] as const;

export type WeatherKind = (typeof WEATHER_KINDS)[number];

export interface WeatherCondition {
    readonly kind: WeatherKind;
    /** The service's words, in its language: "broken clouds". */
    readonly description: string;
    /** A Font Awesome icon name for it, by day or night: "cloud-sun", "cloud-moon". */
    readonly icon: string;
}

export interface WeatherHour {
    readonly time: number;
    readonly temperature: number;
    readonly feelsLike?: number;
    readonly condition: WeatherCondition;
    /** In the hour. */
    readonly rain: number;
    readonly snow: number;
    readonly chance: number;
    readonly wind: number;
    readonly gust?: number;
    readonly windDirection?: number;
    /** The ids of the warnings that overlap it. */
    readonly warnings: readonly string[];
}

export interface WeatherPeriod {
    readonly start: number;
    readonly end: number;
    /** Day 06:00 to 18:00, night 18:00 to 06:00, local time at the place. */
    readonly part: 'day' | 'night';
    /** The day's highest, the night's lowest. */
    readonly temperature: number;
    readonly condition: WeatherCondition;
    /** What it turns into in the slot's second half, where that is another kind. */
    readonly becomes?: WeatherCondition;
    /** Totals for the slot; null where they cannot be known (a night from the daily data). */
    readonly rain: number | null;
    readonly snow: number | null;
    readonly chance: number;
    /** The highest in the slot. */
    readonly wind: number;
    /** The lowest in the slot, where it is worked out from more than one hour: the wind as a range. */
    readonly windLowest?: number;
    readonly gust?: number;
    readonly windDirection?: number;
    /** `hourly`: worked out from the hours; `daily`: taken from the day, beyond the hours. */
    readonly basis: 'hourly' | 'daily';
    readonly warnings: readonly string[];
}

export interface WeatherDay {
    /** The local date at the place: `2026-10-25`. */
    readonly date: string;
    readonly time: number;
    readonly min: number;
    readonly max: number;
    readonly condition: WeatherCondition;
    readonly rain: number;
    readonly snow: number;
    readonly chance: number;
    readonly wind: number;
    readonly gust?: number;
    readonly windDirection?: number;
    readonly sunrise?: number;
    readonly sunset?: number;
    readonly warnings: readonly string[];
}

export interface WeatherWarning {
    readonly id: string;
    readonly sender: string;
    /** Its event, else its tags: "Wind". */
    readonly title: string;
    /** In the connection's language where the sender gives it. */
    readonly description: string;
    readonly tags: readonly string[];
    /** A Font Awesome icon name, by its first tag. */
    readonly icon: string;
    readonly start: number;
    readonly end: number;
}

export interface WeatherUnits {
    readonly temperature: '°C' | '°F';
    readonly wind: 'km/h' | 'mph';
    readonly rain: 'mm' | 'in';
}

export interface WeatherForecast {
    readonly version: 1;
    /** Where, and the place's time zone (IANA): the tile labels times in it. */
    readonly location: { readonly lat: number; readonly lon: number; readonly timeZone: string };
    readonly units: WeatherUnits;
    /** When the driver had it from the service. */
    readonly updated: number;
    /** The hours ahead, from the current one. */
    readonly hourly: readonly WeatherHour[];
    /** Day and night slots, from the current one, about 3 days ahead. */
    readonly periods: readonly WeatherPeriod[];
    /** The days ahead, from today. */
    readonly daily: readonly WeatherDay[];
    /** The warnings in effect or ahead, all-clear notices left out. */
    readonly warnings: readonly WeatherWarning[];
}

const isRecord = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Whether a reading's value is a forecast this contract describes: version 1, with its lists. The
 * tile checks this before drawing, so that a source of another kind shows "No forecast" instead of
 * failing.
 */
export function isWeatherForecast(value: unknown) : value is WeatherForecast {
    return isRecord(value)
        && value.version === 1
        && isRecord(value.location) && typeof value.location.timeZone === 'string'
        && isRecord(value.units)
        && typeof value.updated === 'number'
        && Array.isArray(value.hourly) && Array.isArray(value.periods) && Array.isArray(value.daily) && Array.isArray(value.warnings);
}
